import React, { useMemo, useState } from "react";
import { Table, Form } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "./FilterableTable.css";

// Shared filterable grid for the dashboard detail modals (A/R Over 90,
// Inventory Over 90, ...). One filter row sits beneath the headers, matching the
// Existing Orders list: free text for most columns, a dropdown of the values
// present for low-cardinality ones, and a range picker for dates. Filtering is
// client-side because these detail lists are small.
//
// Columns are described with { key, label, type } where type is one of:
//   undefined/"text" — free-text contains match, left aligned
//   "number"         — right aligned, tabular figures, text contains match
//   "currency"       — formatted as USD, right aligned, text contains match
//   "date"           — MM-DD-YYYY, filtered with a range picker
//   "select"         — dropdown of the distinct values present in the rows
//
// Set `noFilter: true` on a column to show it (with its formatting) but render no
// filter input for it and exclude it from filtering.

const fmtDate = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getMonth() + 1)}-${p(d.getDate())}-${d.getFullYear()}`;
};

const fmtCurrency = (v) => {
  if (v == null || v === "") return "";
  const n = Number(v);
  if (isNaN(n)) return "";
  return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
};

// Compare on the calendar date only — the API returns midnight timestamps, so
// including the time would make an end-of-range date exclude its own day.
const dayOf = (v) => {
  const d = new Date(v);
  return isNaN(d.getTime())
    ? null
    : new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
};

const FilterableTable = ({ columns, rows, emptyText, rowKey }) => {
  const [text, setText] = useState({});
  const [range, setRange] = useState({});

  const options = useMemo(() => {
    const acc = {};
    columns
      .filter((c) => c.type === "select")
      .forEach((c) => {
        acc[c.key] = Array.from(
          new Set(rows.map((r) => r[c.key]).filter((v) => v))
        ).sort();
      });
    return acc;
  }, [columns, rows]);

  const filtered = useMemo(
    () =>
      rows.filter((row) =>
        columns.every((c) => {
          if (c.noFilter) return true;
          if (c.type === "date") {
            const [from, to] = range[c.key] || [null, null];
            if (!from && !to) return true;
            const v = dayOf(row[c.key]);
            if (v === null) return false;
            if (from && v < dayOf(from)) return false;
            if (to && v > dayOf(to)) return false;
            return true;
          }
          const q = (text[c.key] || "").trim().toLowerCase();
          if (!q) return true;
          return String(row[c.key] ?? "")
            .toLowerCase()
            .includes(q);
        })
      ),
    [rows, columns, text, range]
  );

  const cellValue = (row, c) => {
    if (c.type === "date") return fmtDate(row[c.key]);
    if (c.type === "currency") return fmtCurrency(row[c.key]);
    return row[c.key] ?? "";
  };

  return (
    <>
      <div className="ft-result-count">
        Showing {filtered.length} of {rows.length}
      </div>
      <div className="ft-scroll">
        <Table striped bordered hover size="sm" className="ft-table">
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key}>{c.label}</th>
              ))}
            </tr>
            <tr className="ft-filter-row">
              {columns.map((c) => (
                <th key={c.key}>
                  {c.noFilter ? null : c.type === "date" ? (
                    // Render the calendar in a portal so it escapes this grid's
                    // scroll container and the modal. Inside an overflow:auto
                    // container the popper's ResizeObserver repositions as the
                    // scrollbar appears, which retriggers the observer and throws
                    // the benign "ResizeObserver loop completed with undelivered
                    // notifications" error into the dev overlay.
                    <DatePicker
                      selectsRange
                      startDate={(range[c.key] || [])[0]}
                      endDate={(range[c.key] || [])[1]}
                      onChange={(upd) =>
                        setRange((p) => ({ ...p, [c.key]: upd }))
                      }
                      dateFormat="MM-dd-yyyy"
                      placeholderText="MM-DD-YYYY - MM-DD-YYYY"
                      className="form-control form-control-sm"
                      portalId="ft-datepicker-portal"
                      popperProps={{ strategy: "fixed" }}
                      isClearable
                    />
                  ) : c.type === "select" ? (
                    <Form.Select
                      size="sm"
                      value={text[c.key] || ""}
                      onChange={(e) =>
                        setText((p) => ({ ...p, [c.key]: e.target.value }))
                      }
                    >
                      <option value="">Select</option>
                      {(options[c.key] || []).map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </Form.Select>
                  ) : (
                    <Form.Control
                      size="sm"
                      value={text[c.key] || ""}
                      onChange={(e) =>
                        setText((p) => ({ ...p, [c.key]: e.target.value }))
                      }
                    />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="ft-empty">
                  {emptyText}
                </td>
              </tr>
            ) : (
              filtered.map((row, i) => (
                <tr key={(rowKey && row[rowKey]) || i}>
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className={
                        c.type === "number" || c.type === "currency"
                          ? "ft-num"
                          : undefined
                      }
                    >
                      {cellValue(row, c)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
    </>
  );
};

export default FilterableTable;
